import React, { useState } from 'react';
import { Plus, Settings } from 'lucide-react';
import { BankFieldsForm, EMPTY_BANK_FORM, DeleteButton } from './shared';

/**
 * Global bank accounts panel (bank_transfer_global plugin)
 * @param {array} accounts - List of global bank accounts
 * @param {function} onSave - Callback to save/update account (id, formData)
 * @param {function} onDelete - Callback to delete account (id)
 */
export default function GlobalBankPanel({ accounts, onSave, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_BANK_FORM);
  const [editId, setEditId] = useState(null);

  const reset = () => {
    setForm(EMPTY_BANK_FORM);
    setEditId(null);
    setShowForm(false);
  };

  const startEdit = (acc) => {
    setForm({
      accountHolder: acc.account_holder,
      bankName: acc.bank_name,
      accountNumber: acc.account_number,
      routingNumber: acc.routing_number || '',
      currency: acc.currency || 'USD',
      isDefault: !!acc.is_default,
    });
    setEditId(acc.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-2">
      {accounts.map((acc) => (
        <div
          key={acc.id}
          className="flex items-center gap-3 px-3 py-2.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--text-primary)] truncate">
              {acc.account_holder}
              {acc.is_default && (
                <span className="ml-1.5 text-xs font-normal text-[var(--accent-primary)]">
                  (default)
                </span>
              )}
            </p>
            <p className="text-xs text-[var(--text-secondary)]">
              {acc.bank_name} · {acc.currency} · ••••
              {String(acc.account_number).slice(-4)}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => startEdit(acc)}
              className="p-1 rounded text-[var(--text-tertiary)] hover:text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 transition-smooth"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
            <DeleteButton onDelete={() => onDelete(acc.id)} />
          </div>
        </div>
      ))}

      {showForm && (
        <div className="border border-[var(--accent-primary)] rounded-lg p-3 bg-[var(--accent-primary)]/5 space-y-2">
          <p className="text-xs font-semibold text-[var(--text-primary)]">
            {editId ? 'Edit' : 'New'} Global Bank Account
          </p>
          <BankFieldsForm form={form} setForm={setForm} showDefault />
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => {
                onSave(editId, form);
                reset();
              }}
              className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-dark)] text-xs transition-smooth"
            >
              {editId ? 'Save' : 'Add'}
            </button>
            <button
              onClick={reset}
              className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)] transition-smooth"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)] hover:text-[var(--accent-primary-dark)] transition-smooth"
        >
          <Plus className="w-3.5 h-3.5" />
          Add bank account
        </button>
      )}
    </div>
  );
}